import React, { useEffect, useState, createContext, useContext, PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import Validator, { ValidatorFormRules } from './Validator';
import { bindFormChange } from './Util';

const useScript = (url: string) => {
	useEffect(() => {
		const script = document.createElement('script');
		
		script.src = url;
		script.async = true;
		
		document.body.appendChild(script);
		
		return () => {
			document.body.removeChild(script);
		}
	}, [url]);
};

var TitleContext = createContext<{ setTitle: (title: string)=>void }>({ setTitle: (v: string)=>null });
var useTitle = () => useContext(TitleContext);

interface TitleProps extends PropsWithChildren{
	default?: string,
	suffix?: string,
}

var TitleProvider = (props: TitleProps) => {
	var [titleHistory, setTitleHistory] = useState<{ pathname: string, title: string }[]>([]);
	var location = useLocation();

	useEffect(()=>{
		var suffix = props.suffix || 'AREMA Ticket';
		var title = titleHistory.find(a=>a.pathname===location.pathname);
		if(!title || !title.title){
			document.title = suffix;
		}else{
			document.title = `${title.title} - ${suffix}`;
		}
	}, [location, titleHistory]);

	var changeTitle = (title: string)=>{
		if(!location.key) return;
		var hs = [...titleHistory];
		hs = hs.filter(a=>a.pathname!==location.pathname);
		hs.push({ pathname: location.pathname, title });
		setTitleHistory(hs);
	}

	return (
		<TitleContext.Provider value={{ setTitle: changeTitle }}>
			{props.children}
		</TitleContext.Provider>
	)
}

const useValidator = <T,>(initValues: T, rules?: ValidatorFormRules<T>)=>{
	var [data, setData] = useState<T>(initValues);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [errors, setErrors] = useState<{
		[x in keyof T]: boolean
	}>({} as any);

	useEffect(()=>{
		if(data && typeof data==='object' && Object.keys(errors).length===0){
			var err : any = {};
			for(var i in data){
				err[i] = false;
			}
			setErrors(err);
			reset()
		}
	}, []);

	var reset = ()=>{
		if(initValues && typeof initValues==='object'){
			var dt : any = {};
			for(var v in initValues){
				dt[v] = initValues[v];
			}
			setData(dt);
		}
	}

	var validate = (data_override?: any, rules_override?: ValidatorFormRules<T>) : { valid: boolean, prompts: string[], errors: typeof errors }=>{
		if(!rules) return { valid: true, prompts: [], errors };
		// console.log(data);
		// console.log({ ...data, ...data_override }, { ...rules, ...rules_override });
		var vld = Validator({ ...data, ...data_override }, { ...rules, ...rules_override });

		var nw_errors : any = {};
		for(var i in data){
			nw_errors[i] = vld.errors[i];
		}
		setErrors(nw_errors);
		setPrompts(vld.prompts);
		return {
			valid: vld.valid,
			prompts,
			errors: nw_errors
		}
	}

	var onDataChange = (name: keyof T, semantic: boolean=false, target: boolean=false)=>{
		if(errors[name] && rules && rules[name]){
			var valid = Validator.validate(rules[name], data[name], data);
			if(valid){
				setErrors({
					...errors,
					[name]: !valid
				});
			}
		}

		return bindFormChange(data, setData)(name, semantic, target);
	}

	var setValue = (name: keyof T, val: any)=>{
		return setData({
			...data,
			[name]: val
		});
	}

	var updateErrors = (errs: typeof errors)=>{
		setErrors(errs);
	}

	var clearErrors = (initErrors?: typeof errors)=>{
		setErrors(initErrors || {} as any);
	}

	return {
		data,
		errors,
		onDataChange,
		setData,
		setValue,
		prompts,
		reset,
		setPrompts,
		validate,
		setErrors: updateErrors,
		clearErrors,
	}
}

export {
	useScript,
	useTitle,
	useValidator,
	TitleProvider
};